import { Dialog } from "@kobalte/core/dialog";
import { StButton } from "@storytell/ui/buttons";
import { StDropdown, StDropdownItem, StDropdownTrigger } from "@storytell/ui/dropdown";
import { HiSolidPlayCircle, HiSolidPlus } from "solid-icons/hi";
import { TbBellRinging, TbBrandSlack, TbMail, TbMessage2 } from "solid-icons/tb";
import { For, createSignal } from "solid-js";
import { createStore } from "solid-js/store";
import { Dynamic } from "solid-js/web";

/**
 * Mock component for simulating creating a repeatable action from an operation
 */
export const MockSubscribeToConcept = (props: { mount?: Node; conceptName?: string }) => {
  const [open, setOpen] = createSignal(false);

  return (
    <Dialog open={open()} onOpenChange={setOpen}>
      <Dialog.Trigger as={StButton} icon={TbBellRinging}>
        Subscribe to this Concept
      </Dialog.Trigger>
      <Dialog.Portal mount={props.mount}>
        <Dialog.Overlay class="fixed inset-0 z-[9999] bg-black/50 dark:bg-white/10" />
        <div class="fixed z-[9999] inset-0 flex items-center justify-center p-4">
          <Dialog.Content class="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white dark:bg-gray-900 p-6 shadow-2xl border border-gray-200 dark:border-gray-700 relative z-[10000]">
            <SubscribeToConceptPanel mount={props.mount} conceptName={props.conceptName} />
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog>
  );
};

const SubscribeToConceptPanel = (props: { mount?: Node; conceptName?: string }) => {
  const destinations = [
    { text: "Send an SMS", icon: TbMessage2 },
    { text: "Send an Email", icon: TbMail },
    { text: "Send to Slack", icon: TbBrandSlack },
    { text: "Run a custom Action", icon: HiSolidPlayCircle },
  ];

  const [saved, setSaved] = createStore({
    activeTrigger: 0,
    triggerText: null as string | null,
    destinationText: null as string | null,
    activeDestination: 0,
    triggerOpen: false,
  });

  return (
    <div class="space-y-6 bg-white dark:bg-gray-900 relative z-[10001]">
      <div>
        <h3 class="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
          Subscribe to {props.conceptName || "Concept"}
        </h3>
        <p class="text-sm text-gray-600 dark:text-gray-300 mb-4">
          Get notified when relevant data is added to this concept.
        </p>
        <div class="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
          <p class="text-sm text-blue-700 dark:text-blue-300 font-medium">
            ⚠️ Coming Soon - This feature is currently in development
          </p>
        </div>
      </div>

      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Trigger</label>
          <textarea
            onInput={(e) => setSaved("triggerText", e.currentTarget.value)}
            rows={3}
            id="trigger-text"
            placeholder={`What change in ${props.conceptName || "this concept"} should trigger this alert?`}
            class="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={saved.triggerText || ""}
          />
        </div>

        <div>
          <label class="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Destination</label>
          <StDropdown
            portalProps={{ mount: props.mount }}
            contentProps={{ class: "outline-none z-[10000] px-1 py-1 flex flex-col gap-1" }}
            content={
              <>
                <For each={destinations}>
                  {(item, index) => (
                    <StDropdownItem
                      class="flex items-center gap-2 text-sm rounded px-2 py-1
                    transition-colors cursor-pointer outline-none
                    focus:bg-black/10 dark:focus:bg-black/30"
                      onClick={() => setSaved("activeDestination", index())}
                    >
                      <Dynamic component={item.icon} class="size-4" />
                      <span>{item.text}</span>
                    </StDropdownItem>
                  )}
                </For>

                <a
                  href="https://go.Storytell.ai/MCP"
                  target="_blank"
                  rel="noreferrer"
                  class="flex items-center gap-2 text-sm rounded px-2 py-1
                    transition-colors cursor-pointer outline-none text-primary dark:text-blue-300 underline
                    focus:bg-black/10 dark:focus:bg-black/30"
                >
                  <HiSolidPlus class="size-4" />
                  <span>Connect via MCP Server</span>
                </a>
                <a
                  href="https://roadmap.storytell.ai/?b=6849b614a27e78cbf0557010&sortBy=upvotes%3Adesc"
                  target="_blank"
                  rel="noreferrer"
                  class="flex items-center gap-2 text-sm rounded px-2 py-1
                    transition-colors cursor-pointer outline-none text-primary dark:text-blue-300 underline
                    focus:bg-black/10 dark:focus:bg-black/30"
                >
                  <HiSolidPlus class="size-4" />
                  <span>Suggest a new destination</span>
                </a>
              </>
            }
          >
            <StDropdownTrigger
              class="flex items-center gap-2 rounded-lg px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white
        transition-colors cursor-pointer outline-none w-full justify-between
        focus:ring-2 ring-blue-500"
            >
              <div class="flex items-center gap-2">
                <Dynamic component={destinations[saved.activeDestination].icon} class="size-4" />
                <span>{destinations[saved.activeDestination].text}</span>
              </div>
            </StDropdownTrigger>
          </StDropdown>
        </div>
      </div>

      <div class="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <Dialog.CloseButton
          disabled
          class="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
        >
          <HiSolidPlayCircle class="size-4" />
          Subscribe to {props.conceptName || "Concept"}
        </Dialog.CloseButton>
      </div>
    </div>
  );
};
